/**
 * Mini-componente "Fornitore + Famiglia" riusabile per tutti i form materiale.
 *
 * Sostituisce l'Input testo libero del fornitore con un Combobox dei suppliers
 * esistenti + opzione "+ Aggiungi nuovo fornitore". Sotto, il MaterialFamilyField
 * filtrato per il fornitore scelto.
 *
 * Usato in: BoardMaterialForm, FrameMaterialForm, GuideMaterialForm,
 * InsulationMaterialForm, OtherMaterialForm, ScrewMaterialForm (via
 * MaterialBasicInfoSection).
 */
import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Building2 } from 'lucide-react';
import { useSuppliersList } from '@/hooks/useSupplierManagement';
import CreateSupplierDialog from '@/components/suppliers/CreateSupplierDialog';
import MaterialFamilyField from './MaterialFamilyField';
import type { MaterialFormData } from './MaterialFormData';

const ADD_SUPPLIER_SENTINEL = '__add_new_supplier__';

interface SupplierFamilyFieldsProps {
  formData: MaterialFormData;
  setFormData: React.Dispatch<React.SetStateAction<MaterialFormData>>;
  isSubmitting?: boolean;
}

const SupplierFamilyFields: React.FC<SupplierFamilyFieldsProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const { data: suppliers = [] } = useSuppliersList();
  const [showAddSupplierDialog, setShowAddSupplierDialog] = useState(false);

  const currentSupplier = formData.supplier ?? '';
  const supplierInList = suppliers.some((s: { name: string }) => s.name === currentSupplier);

  const handleSupplierChange = (value: string) => {
    if (value === ADD_SUPPLIER_SENTINEL) {
      setShowAddSupplierDialog(true);
      return;
    }
    setFormData(prev => ({
      ...prev,
      supplier: value,
      // Reset famiglia al cambio fornitore (la vecchia non c'entra col nuovo)
      family_id: null,
    }));
  };

  return (
    <>
      <div>
        <Label htmlFor="supplier" className="flex items-center gap-1">
          <Building2 className="h-3 w-3" /> Fornitore *
        </Label>
        <Select
          value={currentSupplier || undefined}
          onValueChange={handleSupplierChange}
          disabled={isSubmitting}
        >
          <SelectTrigger id="supplier">
            <SelectValue placeholder="Seleziona fornitore…" />
          </SelectTrigger>
          <SelectContent>
            {!supplierInList && currentSupplier && (
              <SelectItem value={currentSupplier}>
                {currentSupplier} <span className="text-[9px] text-muted-foreground">(non in catalogo)</span>
              </SelectItem>
            )}
            {suppliers.map((s: { id: string; name: string }) => (
              <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
            ))}
            <div className="border-t mt-1 pt-1">
              <SelectItem value={ADD_SUPPLIER_SENTINEL} className="text-primary font-medium">
                <Plus className="h-3 w-3 mr-1 inline" />
                Aggiungi nuovo fornitore…
              </SelectItem>
            </div>
          </SelectContent>
        </Select>
      </div>

      <MaterialFamilyField
        formData={formData}
        setFormData={setFormData}
        isSubmitting={isSubmitting}
      />

      <CreateSupplierDialog
        open={showAddSupplierDialog}
        onOpenChange={setShowAddSupplierDialog}
        onCreated={(supplier) =>
          setFormData(prev => ({ ...prev, supplier: supplier.name, family_id: null }))
        }
      />
    </>
  );
};

export default SupplierFamilyFields;
